/**
 * The content of /overview, rebuilt on the enterprise domain.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 * www.pulseofp3.org/partner stays live and unchanged. This is the version a
 * visitor sees when they arrive at enterprise.pulseofp3.org, so they can read
 * the institutional story, the pricing and the security page without leaving
 * the domain we sent them to.
 *
 * ── A CORRECTION, Sep 9 2026, WORTH READING BEFORE EDITING ────────────────
 * The first version of this file cut the outcome and benchmarking layers on
 * the grounds that P3 "holds no field anywhere that could contain" a
 * graduation or a job. That was wrong, and it undersold the product. Checked
 * in the code rather than in a summary:
 *
 *   - The CAREER TIMELINE holds `education` and `role` entries with a title,
 *     an organization, start and end years and a past/current stage
 *     (p3-web `features/career/lib/ai-proxy.ts`). That is a career trajectory.
 *   - MILESTONES include graduation and first-role nodes, and outcome-bearing
 *     ones set `completionNoteRequired` (p3-mobile
 *     `MarkMilestoneCompleteSheet.tsx`), so a student must say where they
 *     started work, or which major they chose, before the milestone completes.
 *   - CROSS-INSTITUTION COMPARISON is structurally available: P3 has students
 *     from many institutions in one dataset, and the dashboard already ships
 *     a compare control.
 *
 * So the layers are here, with the qualifier the marketing page never had:
 * outcomes are STUDENT REPORTED AND UNVERIFIED, and the volume is early. The
 * page says both, because a buyer who discovers "self-reported" later feels
 * misled, and a buyer who reads it here does not.
 *
 * The two things that remain genuinely absent, and must not creep back:
 *   - VERIFICATION. No registrar or employer feed, no enrollment check, no
 *     GPA, no salary, no credential award from an awarding body.
 *   - NAMED PEER COMPARISON. Aggregate P3 comparison, yes. "You versus
 *     Howard", no, and it would breach the suppression commitment anyway.
 *
 * ── SEP 9 2026, LAYOUT ────────────────────────────────────────────────────
 * Thomas asked for the marketing site's layouts on this page: the partner
 * page's Why Now icon grid, its Who We Serve photo cards, its "four layers of
 * data" 2x2, and the for-students page's "four simple steps". Layout only.
 * Every sentence below is still this domain's and is checked against /trust.
 */

import { formatCountAtLeast, formatMoney } from "~/app/lib/format";
import { BANDS, PATHWAY_COUNT, SUPPRESSION_FLOOR } from "~/app/lib/pricing";

/** The icons the Why now cards draw. Keys, not markup: the page owns the SVG. */
export type ReasonIcon = "ai" | "reporting" | "network";

export interface Reason {
  readonly icon: ReasonIcon;
  readonly title: string;
  readonly detail: string;
}

/**
 * Why now. Three external facts about the sector, none of them a claim about
 * P3, so nothing here needs P3 to hold data it does not have. One row of
 * three. Two more left on Sep 19 2026 (Thomas): "Students are asking what
 * tuition bought" and "Advising does not scale".
 */
export const WHY_NOW: readonly Reason[] = [
  {
    icon: "ai",
    title: "AI is taking the entry level rung",
    detail: "The jobs students used to start in are the ones being automated first.",
  },
  {
    icon: "reporting",
    title: "Outcome reporting is now the ask",
    detail: "Accreditors and funders want evidence a support program did something.",
  },
  {
    icon: "network",
    title: "The network gap is the real gap",
    detail: "Most roles are filled through people a first-generation student has never met.",
  },
];

export interface Audience {
  readonly kind: string;
  readonly roles: string;
  /** Optional since Sep 21 2026: all three cards dropped theirs. */
  readonly lede?: string;
  readonly points: readonly string[];
  readonly image: string;
  readonly alt: string;
}

/**
 * Who it is for. Bullets are what a partner actually receives, four to a
 * card so the three cards end level. Outcome and comparison carry the
 * qualifier the marketing page never had: student reported, aggregate. The
 * one claim that stays out is verified DEI reporting, because P3 collects no
 * demographics at all and that absence is real.
 */
export const AUDIENCES: readonly Audience[] = [
  {
    kind: "Universities and colleges",
    roles: "Student Success · Career Services · Alumni",
    points: [
      "Mentors from industry, not only your alumni",
      "Your claimed roster, and how it grows",
      "Where they end up, in their own words",
    ],
    image: "/img/institution-hbcu.jpg",
    alt: "Students on a university campus walkway",
  },
  {
    kind: "Districts and community programs",
    roles: "Youth Programs · After School · Nonprofits",
    points: [
      `${PATHWAY_COUNT} career pathways, open to every student`,
      // One line per bullet on a desktop card (Sep 19 2026, Thomas), measured
      // at 282px at 1280 and up.
      "Scholarships, internships, jobs: 200+ a month",
      "Reporting you can put in a grant report",
    ],
    image: "/img/institution-youth.jpg",
    alt: "Young people working together at a table",
  },
  {
    kind: "Employers and foundations",
    roles: "Talent · Community Investment · Foundations",
    points: [
      "Your staff join as mentors, approved by P3",
      // What an employer is actually buying: the hours of people it already
      // has, multiplied (Thomas, Sep 21 2026). It replaced "Students reached,
      // no personal data shared", which described a privacy property rather
      // than a reason to fund anything.
      "Your senior people reach students at scale",
      "A cohort you fund, reported like any partner",
    ],
    image: "/img/institution-corporate.jpg",
    alt: "Professionals talking in an office",
  },
];

export interface Layer {
  readonly index: string;
  readonly title: string;
  readonly detail: string;
  readonly items: readonly string[];
  /** The fourth card sits on the dark gradient and carries a pill. */
  readonly moat?: boolean;
  readonly pill?: string;
}

/**
 * What P3 measures: four layers, the marketing page's 2x2, with the fourth
 * card on the dark gradient where the marketing page puts its "data moat".
 * On /security since Sep 19 2026 (Thomas), between the documents and the
 * gaps; it was /overview's "Your reporting" section until then.
 * The pill on that card says what the marketing page's did not: these are
 * student reported. Layers 01 to 03 are platform facts P3 observes directly;
 * 04 is what students report about themselves, and says so on its face.
 *
 * The claim that is gone for good is CAUSATION. "The causal link between
 * mentorship activity and real outcomes" is not something a self-reported
 * milestone can establish, and it is the one sentence a research office
 * would take apart.
 */
export const LAYERS: readonly Layer[] = [
  {
    index: "01",
    title: "Who claimed you",
    detail: "The students who connected a P3 account to your institution.",
    items: [
      "Your claimed roster, week by week",
      "How they joined: link, code or app",
      "Seats used against your plan",
      `Nothing reported below ${SUPPRESSION_FLOOR} students`,
    ],
  },
  {
    index: "02",
    title: "What they do on P3",
    detail: "Activity in the app from day one, with or without a mentor.",
    items: [
      "Opportunities applied to through the app",
      "Questions asked of mentors",
      "Students active in the app, and how often",
      "Mentorships held",
    ],
  },
  {
    index: "03",
    title: "What they say they need",
    detail: "Self-reported, from the profile each student fills in themselves.",
    items: [
      "The support areas they chose",
      "Education goal and industry interest",
      "How often they want to meet",
      "Milestones marked complete",
    ],
  },
  {
    index: "04",
    title: "Where they end up",
    // Sep 19 2026 (Thomas: "confusing"; was "Outcome milestones ask the
    // student to say where."). Two lines at most, at every width.
    detail: "Students report where they go next, in the P3 app.",
    items: [
      "Program started, as students report it",
      "Major declared and a first full-time role",
      "Internships and research experience",
      // Sep 19 2026: was "Set against all P3 students", which only Mentorship
      // reach offers; nothing compares outcomes with the platform.
      "Career timelines, past, current and planned",
    ],
    moat: true,
    pill: "Student reported",
  },
];

/* The boundary sentence that used to sit under the grid left on Sep 9 2026 PM
   (Thomas). The fourth card's own detail and pill carry "student reported",
   and /trust carries the full ledger. */

export interface Step {
  readonly n: string;
  readonly title: string;
  readonly detail: string;
}

/** How it works: four cards, the for-students page's shape. Card copy at
 *  most twelve words, per the text budget. */
export const HOW_IT_WORKS: readonly Step[] = [
  {
    n: "1",
    title: "Choose a capacity",
    detail: "Pick the size that fits the program. Nothing is metered.",
  },
  {
    n: "2",
    title: "Share your join link",
    /* "a poster" until Sep 14 2026 (Thomas): the claim centre gives a QR code
       to download, and the print-ready poster is still planned. "Your
       advisors" became "Advisors" to keep the card inside 13 words. */
    detail: "A link, a six character code and a QR code. Advisors send it.",
  },
  {
    n: "3",
    title: "Your students connect",
    detail: "A student connects their own account, and can undo it any time.",
  },
  {
    n: "4",
    title: "Reporting turns on",
    detail: `Your dashboard opens once ${SUPPRESSION_FLOOR} students have connected.`,
  },
];

/* ==========================================================================
   Inside the dashboard (Thomas, Sep 19 2026): the feature showcase, between
   How it works and Try it now.

   One real screen and six drawings. The screen is the matching board,
   captured from the sample at /demo/matching; the drawings are simplified
   pictures of features that are live today, each checked against the code
   (Docs/P3 Enterprise/Enterprise-Feature-Showcase.md). A feature that is
   off, planned or rarely has enough students to show a figure stays out:
   withdrawing a request, single sign-on, opportunities opened, working in
   their own field. Copy is a title and one line; the pictures carry it.
   ========================================================================== */

export type ShowcaseArt = "link" | "waiting" | "cohorts" | "outcomes" | "packet" | "trace";

export interface ShowcaseCard {
  readonly art: ShowcaseArt;
  /** The step tints of How it works, so the two sections read as one page. */
  readonly tone: 1 | 2 | 3 | 4;
  readonly title: string;
  readonly detail: string;
  /** Kept on a phone (Thomas, Sep 19 2026: "too long with too much
   *  scrolling"): the rope card, the join link, cohorts and outcomes. The
   *  other three show from 600px up. */
  readonly onPhone: boolean;
}

export const SHOWCASE_CARDS: readonly ShowcaseCard[] = [
  // Every detail fits one line in the card from 1024px up (Sep 19 2026,
  // Thomas), measured at 12.75px against the card's 266px column there.
  { art: "link", tone: 2, title: "One join link", detail: "Students and mentors join from one link.", onPhone: true },
  { art: "waiting", tone: 4, title: "Facilitate networking", detail: "Who joined, and who still needs a mentor.", onPhone: false },
  { art: "cohorts", tone: 1, title: "Your own cohorts", detail: "One department, term or program at a time.", onPhone: true },
  { art: "outcomes", tone: 3, title: "Track student outcomes", detail: "From a first program to a first full-time role.", onPhone: true },
  { art: "packet", tone: 1, title: "A board packet in one click", detail: "A PDF, a CSV, or a link to the exact view.", onPhone: false },
  { art: "trace", tone: 2, title: "Every number explains itself", detail: "Source, denominator and limits in one click.", onPhone: false },
];

export const SHOWCASE = {
  tag: "Inside the dashboard",
  /* Not "Built for..." any more (Thomas, Sep 21 2026): "Who it is for" opens
     "Built for those who believe in student success" two sections above, and
     two headings in a row on the same construction read as one idea told
     twice. This one says what the screen DOES, which is also what the sub
     line promises. */
  lead: "Run the whole program from",
  accent: "one screen",
  sub: "Match students with mentors, see who is waiting, and report upward.",
  hero: {
    tag: "Matching",
    /* "Ask for a mentorship with a rope" until Sep 19 2026 (Thomas: reword,
       and the last word in P3 red). "Connect", not "match": the line sends a
       request, and the mentor accepts or declines it in the P3 app
       (Enterprise REVIEW-NOTES round 44). "Your": the board lists only the
       institution's mentors, which the gateway enforces. Two lines at every
       width ("your own" left "with your" alone on the middle line). */
    lead: "Connect students with your",
    accent: "mentors",
    /* Sep 19 2026 (Thomas picked it). The board draws a line only to the
       institution's own mentors (the gateway refuses any other,
       MENTOR_NOT_AFFILIATED), so the sentence says nothing about P3's
       mentor pool. "Follow it": the line is dashed while the mentor
       decides and solid once they accept, which the legend below names.
       Two lines at 1440, 1024 and 390 in the copy's 315px column. */
    detail: "Draw a line from a student to a mentor. Follow it from request to mentorship.",
    legend: [
      { line: "solid", label: "Mentoring" },
      // "Confirming match" since Sep 19 2026 (Thomas), here and on the board
      // itself (pages/AdminMatching.tsx), so the screen beside it agrees.
      { line: "dashed", label: "Confirming match" },
    ],
    cta: "See the demo",
    href: "/demo/matching",
    address: "enterprise.pulseofp3.org/demo/matching",
    image: "/img/showcase-matching.webp",
    imageWidth: 1600,
    imageHeight: 1027,
    alt: "The matching board in the sample dashboard: students on the left, the institution's own mentors on the right, a solid line for a mentorship, a dashed line for a request waiting on the mentor, and dotted lines for pairs the students made in the app.",
  },
  cards: SHOWCASE_CARDS,
} as const;

/**
 * The /overview masthead's headline and sub, in one place since Sep 9 2026
 * (night): the sign-in's brand panel carries the same headline, so the two
 * cannot drift. One accent phrase, rendered by HeroAccent.
 *
 * Sep 12 2026 (Thomas): "The career accelerator for your students", for parity
 * with the member app's sign-in ("The career accelerator for the next
 * generation"). index.html's link tags and scripts/og-enterprise.html say the
 * same.
 */
export const HERO = {
  lead: "The career accelerator",
  accent: "for your students",
  sub: "Mentors from industry, curated opportunities and career pathways, free for every student. You get an honest count of who used it.",
} as const;

export interface HeroStatCopy {
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}

/**
 * What a plan works out to per student, per month, on every priced plan and
 * on either cadence, rounded UP to the next whole dollar so "Under $3" is true
 * of the dearest one ($400 a month for 150 students is $2.67). Derived, never
 * typed: if a band is repriced past it, the figure moves with it.
 *
 * An equivalent and never a rate (pricing.ts `perStudentEquivalent`): nothing
 * is metered per student, which is why the stat says "equivalent".
 */
export function perStudentMonthlyCeiling(): number {
  const equivalents = BANDS.flatMap((band) =>
    band.capacity === null
      ? []
      : [
          ...(band.monthly === null ? [] : [band.monthly / band.capacity]),
          ...(band.annual === null ? [] : [band.annual / 12 / band.capacity]),
        ],
  );
  return Math.floor(Math.max(...equivalents)) + 1;
}

/** The registered-user floor the public site states (Thomas, Sep 9 2026). Updated by hand. */
export const REGISTERED_USERS_FLOOR = 1200;

const PER_STUDENT: HeroStatCopy = {
  label: "Per student, per month",
  value: `Under ${formatMoney(perStudentMonthlyCeiling())}`,
  detail: "Equivalent on every plan",
};

/**
 * The /overview hero's four stats (Thomas, Sep 12 2026). "Free" left the row
 * because the page converts paying institutions, and the mentor count left it
 * because a dated snapshot goes stale. Every one is checkable and none is a
 * traction claim.
 */
export const OVERVIEW_STATS: readonly HeroStatCopy[] = [
  PER_STUDENT,
  {
    label: "Career pathways",
    value: String(PATHWAY_COUNT),
    detail: "Open with no mentor required",
  },
  {
    label: "Setup",
    value: "No IT work",
    detail: "Students join from a link",
  },
  {
    label: "Admin accounts",
    value: "Unlimited",
    detail: "Owner, admin and viewer roles",
  },
];

/**
 * The sign-in panel's three stats, a value and a label each, as on the member
 * app's sign-in (Thomas, Sep 12 2026). Thomas chose these apart from
 * /overview's, including the one traction figure on this host: the registered
 * users the public site states.
 */
export const LOGIN_STATS: readonly HeroStatCopy[] = [
  PER_STUDENT,
  {
    label: "Figures refresh",
    value: "Hourly",
    detail: "Recomputed from student records",
  },
  {
    label: "Registered users",
    value: formatCountAtLeast(REGISTERED_USERS_FLOOR),
    detail: "Students and mentors on P3",
  },
];

/**
 * The platform page's demo band (.wa-demo-band on www.pulseofp3.org/platform),
 * brought onto /overview at Thomas's ask (Sep 9 2026, night), with the link
 * pointed at this host's sample dashboard. The copy is the platform page's.
 */
export const DEMO_BAND = {
  tag: "See for yourself",
  title: "Try it now,",
  accent: "no account needed.",
  lede: "One click opens a full working preview with sample data. Nothing to install, nothing to sign up for.",
  cta: "Try the Demo",
  href: "https://enterprise.pulseofp3.org/demo",
} as const;
